import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, IconButton, Paper, Portal, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";

const DISMISSED_KEY = "neozapp:pwa-install-dismissed-until:v1";
const SEEN_KEY = "neozapp:pwa-install-seen:v1";
const DISPLAY_DELAY = 6000;
const DISMISS_DURATION = 30 * 24 * 60 * 60 * 1000;

const readStorage = (area, key) => {
  try {
    return window[area].getItem(key);
  } catch (_) {
    return null;
  }
};

const writeStorage = (area, key, value) => {
  try {
    window[area].setItem(key, value);
  } catch (_) {
    // The notice still works when private browsing blocks persistent storage.
  }
};

const pageIsBusy = () => {
  if (typeof document === "undefined" || document.visibilityState === "hidden") return true;
  const focused = document.activeElement;
  if (focused && (focused.matches("input, textarea, select")
    || focused.closest('[contenteditable]:not([contenteditable="false"])'))) return true;

  const viewport = window.visualViewport;
  if (viewport && viewport.scale === 1 && window.innerHeight - viewport.height > 100) return true;

  return Array.from(document.querySelectorAll("body > .MuiDialog-root, body > .MuiPopover-root"))
    .some(modal => {
      if (modal.hasAttribute("data-pwa-install-guide")) return false;
      const style = window.getComputedStyle(modal);
      return style.display !== "none" && style.visibility !== "hidden";
    });
};

const getSteps = () => {
  const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(userAgent)) return [
    "Abra Compartilhar. No Safari, essa opção também pode estar no Menu da Página.",
    "Toque em “Adicionar à Tela de Início”.",
    "Mantenha “Abrir como App da Web” ativado e toque em “Adicionar”.",
  ];
  if (/Android/i.test(userAgent)) return [
    "No Chrome, abra o menu de três pontos.",
    "Toque em “Instalar e criar atalho” ou “Adicionar à tela inicial”, conforme aparecer.",
    "Confirme em “Instalar” ou “Adicionar”.",
  ];
  return [
    "Abra o menu do navegador e procure “Adicionar à tela inicial” ou “Instalar app”.",
    "Siga a confirmação mostrada pelo navegador.",
  ];
};

const useStyles = makeStyles(theme => ({
  notice: {
    position: "fixed",
    left: 12,
    right: 12,
    bottom: "calc(12px + var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)))",
    maxWidth: 420,
    margin: "0 auto",
    padding: 16,
    borderRadius: 16,
    border: 0,
    boxSizing: "border-box",
    backgroundColor: theme.palette.type === "dark" ? "#1c1c1c" : "#ffffff",
    color: theme.palette.text.primary,
    backdropFilter: "none",
    boxShadow: "0 8px 28px rgba(0, 0, 0, 0.28)",
    zIndex: theme.zIndex.snackbar,
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingRight: 32,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "22px",
  },
  icon: { color: theme.palette.primary.main, fontSize: 22, flexShrink: 0 },
  copy: {
    marginTop: 8,
    fontSize: 16,
    lineHeight: "22px",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.75)" : "#595959",
  },
  close: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 44,
    height: 44,
    padding: 10,
    color: theme.palette.text.secondary,
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  actions: { display: "flex", alignItems: "center", gap: 8, marginTop: 12 },
  primary: {
    minHeight: 44,
    padding: "8px 12px",
    borderRadius: 12,
    textTransform: "none",
    fontSize: 16,
    fontWeight: 600,
    backgroundColor: "#0F7A3E",
    color: "#ffffff",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#0b6332", boxShadow: "none" },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  secondary: {
    minHeight: 44,
    padding: "8px 12px",
    borderRadius: 12,
    fontSize: 16,
    textTransform: "none",
    color: theme.palette.text.secondary,
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  dialogContainer: {
    alignItems: "flex-end",
    padding: "12px 12px calc(12px + var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)))",
    boxSizing: "border-box",
  },
  dialogPaper: {
    width: "100%",
    maxWidth: 420,
    margin: 0,
    padding: 20,
    border: 0,
    borderRadius: 16,
    boxSizing: "border-box",
    maxHeight: "calc(100vh - 24px - var(--safe-area-top, 0px) - var(--safe-area-bottom, 0px))",
    "@supports (height: 100dvh)": {
      maxHeight: "calc(100dvh - 24px - var(--safe-area-top, 0px) - var(--safe-area-bottom, 0px))",
    },
    backgroundColor: theme.palette.type === "dark" ? "#1c1c1c" : "#ffffff",
    color: theme.palette.text.primary,
    backdropFilter: "none",
  },
  guideTitle: { paddingRight: 32, fontSize: 20, fontWeight: 600, lineHeight: "26px" },
  steps: {
    margin: "20px 0",
    paddingLeft: 24,
    fontSize: 16,
    lineHeight: "24px",
    "& li + li": { marginTop: 12 },
    "& li::marker": { color: theme.palette.text.secondary, fontWeight: 600 },
  },
}));

export default function PwaInstallNotice({ enabled = false, blocked = false }) {
  const classes = useStyles();
  const shownHere = useRef(false);
  const pageFocus = useRef(null);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(() => typeof document === "undefined" || pageIsBusy());

  const dismiss = useCallback(() => {
    writeStorage("localStorage", DISMISSED_KEY, String(Date.now() + DISMISS_DURATION));
    setDismissed(true);
    setNoticeOpen(false);
    setGuideOpen(false);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let deferredCheck;
    const check = () => setBusy(pageIsBusy());
    const afterInteraction = () => {
      window.clearTimeout(deferredCheck);
      deferredCheck = window.setTimeout(check, 0);
    };
    check();
    document.addEventListener("focusin", check);
    document.addEventListener("focusout", afterInteraction);
    document.addEventListener("click", afterInteraction, true);
    document.addEventListener("visibilitychange", check);
    const viewport = window.visualViewport;
    if (viewport) viewport.addEventListener("resize", check);
    window.addEventListener("appinstalled", dismiss);
    return () => {
      window.clearTimeout(deferredCheck);
      document.removeEventListener("focusin", check);
      document.removeEventListener("focusout", afterInteraction);
      document.removeEventListener("click", afterInteraction, true);
      document.removeEventListener("visibilitychange", check);
      if (viewport) viewport.removeEventListener("resize", check);
      window.removeEventListener("appinstalled", dismiss);
    };
  }, [enabled, dismiss]);

  useEffect(() => {
    if (!enabled || blocked) {
      setNoticeOpen(false);
      setGuideOpen(false);
    }
  }, [enabled, blocked]);

  useEffect(() => {
    const snoozed = Number(readStorage("localStorage", DISMISSED_KEY)) > Date.now();
    const seenElsewhere = !shownHere.current && readStorage("sessionStorage", SEEN_KEY) === "1";
    if (!enabled || blocked || busy || guideOpen || dismissed || snoozed || seenElsewhere) {
      setNoticeOpen(false);
      return undefined;
    }
    if (shownHere.current) return undefined;
    const timer = window.setTimeout(() => {
      if (pageIsBusy()) {
        setBusy(true);
        return;
      }
      shownHere.current = true;
      pageFocus.current = document.activeElement;
      writeStorage("sessionStorage", SEEN_KEY, "1");
      setNoticeOpen(true);
    }, DISPLAY_DELAY);
    return () => window.clearTimeout(timer);
  }, [enabled, blocked, busy, guideOpen, dismissed]);

  if (!enabled) return null;

  return (
    <>
      {noticeOpen && !blocked && !busy && !guideOpen && (
        <Portal>
          <Paper elevation={0} className={classes.notice} data-pwa-install-notice role="status" aria-live="polite">
            <Typography component="h2" className={classes.title}>
              <GetAppOutlinedIcon className={classes.icon} />
              Use o NeoZapp como app
            </Typography>
            <IconButton className={classes.close} onClick={dismiss} aria-label="Dispensar dica de instalação">
              <CloseIcon />
            </IconButton>
            <Typography className={classes.copy}>
              Para uma melhor experiência no celular, adicione o NeoZapp à tela inicial.
            </Typography>
            <Box className={classes.actions}>
              <Button className={classes.primary} onClick={() => { setNoticeOpen(false); setGuideOpen(true); }}>
                Como adicionar
              </Button>
              <Button className={classes.secondary} onClick={dismiss}>Agora não</Button>
            </Box>
          </Paper>
        </Portal>
      )}
      <Dialog
        open={guideOpen && !blocked}
        onClose={dismiss}
        maxWidth={false}
        classes={{ container: classes.dialogContainer, paper: classes.dialogPaper }}
        aria-labelledby="pwa-install-guide-title"
        aria-describedby="pwa-install-guide-description"
        TransitionProps={{
          onExited: () => {
            const target = pageFocus.current;
            if (target && target.isConnected) target.focus({ preventScroll: true });
          },
        }}
        data-pwa-install-guide
      >
        <Typography component="h2" id="pwa-install-guide-title" className={classes.guideTitle}>
          Adicionar à tela inicial
        </Typography>
        <IconButton className={classes.close} onClick={dismiss} aria-label="Fechar instruções de instalação">
          <CloseIcon />
        </IconButton>
        <Typography id="pwa-install-guide-description" className={classes.copy}>
          Siga os passos no navegador para adicionar um ícone do NeoZapp à tela inicial.
        </Typography>
        <ol className={classes.steps}>
          {getSteps().map(step => <li key={step}>{step}</li>)}
        </ol>
        <Button className={classes.primary} fullWidth onClick={dismiss}>Entendi</Button>
      </Dialog>
    </>
  );
}
